import ApiService from './api.service';
import type { ListRecordsResponse, SingleRecordResponse } from './pipelineNode.service';

export type { ListRecordsResponse };

export type GoalDirection = 'ABOVE' | 'BELOW';
export type GoalSource = 'MANUAL' | 'CSV';

export interface GoalDimensionValue {
  dimension_id: string;
  /**
   * For calendar dimensions this is an ISO date ('2026-05-01'); the backend
   * canonicalizes whatever the user typed so it lines up with the warehouse.
   */
  value: string;
}

export interface Goal {
  id?: string;
  metric_id: string;
  dimension_values: GoalDimensionValue[];
  /** Derived server-side; the (metric_id, signature) pair is the uniqueness key. */
  dimension_signature?: string;
  value: number;
  direction: GoalDirection;
  notes?: string;
  source?: GoalSource;
  upload_batch_id?: string | null;
  created_by?: string | null;
  last_updated?: string | null;
}

/**
 * What you send when creating/updating a goal.
 *
 * `value` is deliberately looser than on `Goal`: the backend parses currency and
 * thousands separators, so the UI can pass exactly what the user typed
 * ('$120,000') rather than pre-parsing it and disagreeing about the rules.
 */
export interface GoalWritePayload {
  metric_id: string;
  dimension_values: GoalDimensionValue[];
  value: string | number;
  direction?: GoalDirection;
  notes?: string;
}

export interface GoalImportRowError {
  row: number;
  message: string;
}

export interface GoalImportResult {
  created: number;
  updated: number;
  errors: GoalImportRowError[];
  /** Set when more rows failed than `errors` lists — the response caps them. */
  errors_truncated?: number;
  error_count?: number;
  validate_only: boolean;
}

export interface GoalBulkResult {
  records: Goal[];
  errors: { index: number; message: string }[];
}

export class GoalService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    /**
     * Restrict to these metrics. Filtered server-side on purpose: the list is
     * paginated, so narrowing the current page client-side would hide matches
     * on every other page while still claiming to show "all" of them.
     */
    metricIds?: string[];
    filters?: Record<string, unknown>;
  }): Promise<ListRecordsResponse<Goal>> {
    const { page = 1, pageSize = 500, metricIds, filters = {} } = options || {};

    const params: Record<string, unknown> = {
      page,
      limit: pageSize,
      ...filters,
    };

    if (metricIds?.length) {
      params.filter = JSON.stringify({ metric_id: { $in: metricIds } });
    }

    const result = await ApiService.getInstance().request(
      'GET',
      '/goals',
      params
    ) as ListRecordsResponse<Goal>;

    return result;
  }

  /**
   * `{metric_id: count}` for every metric that has goals.
   *
   * Metrics with no goals are absent, not zero — the caller has the metric list
   * and this only knows what goals exist. Comes from a grouped aggregate rather
   * than being counted off `find()`, which only ever returns one page.
   */
  async fetchCountsByMetric(): Promise<Record<string, number>> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/goals/counts'
    ) as { counts?: Record<string, number> };
    return result.counts || {};
  }

  async create(goal: GoalWritePayload): Promise<Goal> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/goals',
      goal
    ) as SingleRecordResponse<Goal>;
    return result.record;
  }

  async update(id: string, updates: Partial<GoalWritePayload>): Promise<Goal> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/goals/${id}`,
      updates
    ) as SingleRecordResponse<Goal>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request('DELETE', `/goals/${id}`);
  }

  async bulkUpsert(goals: GoalWritePayload[]): Promise<GoalBulkResult> {
    return await ApiService.getInstance().request(
      'POST',
      '/goals/bulk',
      { goals }
    ) as GoalBulkResult;
  }

  /**
   * Import goals from CSV text.
   *
   * Always call with `validateOnly: true` first and show the user the errors —
   * a bad column header rejects the whole file, and bad rows are reported
   * individually so good rows still land.
   */
  async importCsv(content: string, validateOnly: boolean): Promise<GoalImportResult> {
    return await ApiService.getInstance().request(
      'POST',
      `/goals/upload?validate_only=${validateOnly ? 'true' : 'false'}`,
      { content }
    ) as GoalImportResult;
  }

  async deleteBatch(batchId: string): Promise<{ deleted: number }> {
    return await ApiService.getInstance().request(
      'DELETE',
      `/goals/batch/${batchId}`
    ) as { deleted: number };
  }

  /**
   * The starter CSV, as a Blob for the caller to save.
   *
   * Goes through `requestBlob` rather than an `<a href>` — the endpoint is behind
   * `@with_auth_context()` and an anchor sends no Authorization header, so a link
   * just 401s. The template's column names come from the tenant's own dimensions,
   * so it can't be a static asset either.
   */
  async fetchTemplate(): Promise<Blob> {
    return await ApiService.getInstance().requestBlob('/goals/template');
  }
}

let instance: GoalService | null = null;

export const getGoalService = (): GoalService => {
  if (!instance) {
    instance = new GoalService();
  }
  return instance;
};

/**
 * Percentage over/under plan, and whether that reads as good.
 *
 * `direction` is what makes a cost goal ('BELOW') colour green when actual comes
 * in under plan, while a revenue goal ('ABOVE') colours green when it comes in
 * over. Returns null when there's nothing meaningful to show.
 */
export const evaluateGoal = (
  actual: number | null | undefined,
  goal: number | null | undefined,
  direction: GoalDirection = 'ABOVE'
): { pctDiff: number; isFavorable: boolean } | null => {
  if (actual == null || goal == null) return null;
  if (!Number.isFinite(actual) || !Number.isFinite(goal)) return null;
  // A zero goal makes "% of plan" undefined — fall back to direction only.
  if (goal === 0) {
    if (actual === 0) return { pctDiff: 0, isFavorable: true };
    return {
      pctDiff: Number.POSITIVE_INFINITY,
      isFavorable: direction === 'ABOVE' ? actual > 0 : actual < 0,
    };
  }

  const pctDiff = ((actual - goal) / Math.abs(goal)) * 100;
  const isFavorable = direction === 'ABOVE' ? actual >= goal : actual <= goal;
  return { pctDiff, isFavorable };
};
